import React, { memo, useMemo } from 'react'
import { ScaleLoader } from 'react-spinners'
import { useTheme } from 'styled-components'
import {
    StyledMessage,
    StyledLoadingModal,
    StyledProgressContainer,
    StyledProgressBar,
    StyledProgressFill,
    StyledProgressText,
    StyledCancelButton,
} from './styles'

interface ProgressInfo {
    current: number
    total: number
    processed: number
}

interface Props {
    message: string
    isShown: boolean
    progress?: ProgressInfo
    onCancel?: () => void
}

// Memoized progress bar component to prevent unnecessary re-renders
const ProgressBar = memo(function ProgressBar({
    progress,
}: {
    progress: ProgressInfo
}) {
    const progressPercent = useMemo(
        () => Math.round((progress.current / progress.total) * 100),
        [progress.current, progress.total]
    )

    return (
        <StyledProgressContainer>
            <StyledProgressBar>
                <StyledProgressFill $percent={progressPercent} />
            </StyledProgressBar>
            <StyledProgressText>
                Batch {progress.current} of {progress.total} (
                {progress.processed.toLocaleString()} tickers processed)
            </StyledProgressText>
        </StyledProgressContainer>
    )
})

// Memoized cancel button to prevent re-renders when callback identity changes
const CancelButton = memo(function CancelButton({
    onCancel,
}: {
    onCancel: () => void
}) {
    return <StyledCancelButton onClick={onCancel}>Cancel</StyledCancelButton>
})

function LoadingModal({ message, isShown, progress, onCancel }: Props) {
    const theme = useTheme()

    if (!isShown) {
        return null
    }

    return (
        <StyledLoadingModal>
            <ScaleLoader color={theme.colours.font.green} loading={true} />
            <StyledMessage>{message}</StyledMessage>
            {progress && progress.total > 0 && (
                <ProgressBar progress={progress} />
            )}
            {onCancel && <CancelButton onCancel={onCancel} />}
        </StyledLoadingModal>
    )
}

// Memoize the entire component - only re-render when props change
export default memo(LoadingModal)
